import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, List, message, Skeleton } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import type {
  InternshipApplication,
  ReducedInternship,
  User,
} from '../../Types/interfaces';
import {
  AcceptInternshipApplication,
  GetInternshipsApplicationsAndApplicantsOfOrganization,
  RejectInternshipApplication,
} from '../../helper/apiHelper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { getErrorMessage } from '../../helper/errorHelper';

const ApplicantsComponent: React.FC = () => {
  const loggedInUser = useCurrentUser();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser?.id);
  const queryClient = useQueryClient();

  const { data: organizationData = EMPTY_ARRAY, isLoading } = useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIPS_APPLICATIONS_AND_APPLICANTS_OF_ORGANIZATION,
      userOrganizations,
    ],
    queryFn: async () => {
      const promises = userOrganizations?.map(async (organization) => {
        const response =
          await GetInternshipsApplicationsAndApplicantsOfOrganization(
            organization.id
          );

        return {
          applications: response.applications,
          pendingApplicants: response.applicants,
          internships: response.internships,
        };
      });

      return await Promise.all(promises ?? []);
    },
    enabled: !!userOrganizations,
  });

  const applications: InternshipApplication[] = organizationData.flatMap(
    (orgData) => orgData.applications
  );
  const applicants: User[] = organizationData.flatMap(
    (orgData) => orgData.pendingApplicants
  );
  const internships: ReducedInternship[] = organizationData.flatMap(
    (orgData) => orgData.internships
  );

  const onAccept = async (
    organizationId: number,
    offerId: number,
    applicationId: number
  ): Promise<void> => {
    try {
      await AcceptInternshipApplication(organizationId, offerId, applicationId);
      messageApi.success(t('applicantsComp.acceptSuccess'));
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.INTERNSHIPS_APPLICATIONS_AND_APPLICANTS_OF_ORGANIZATION,
          userOrganizations,
        ],
      });

      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_COUNT,
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDENT_COUNT,
      });
    } catch (error) {
      console.error(error);
      messageApi.error(getErrorMessage(error, 'applicantsComp.acceptError'));
    }
  };

  const onReject = async (
    organizationId: number,
    offerId: number,
    applicationId: number
  ): Promise<void> => {
    try {
      await RejectInternshipApplication(organizationId, offerId, applicationId);
      messageApi.success(t('applicantsComp.rejectSuccess'));

      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.INTERNSHIPS_APPLICATIONS_AND_APPLICANTS_OF_ORGANIZATION,
          userOrganizations,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.APPLICATION_COUNT,
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.STUDENT_COUNT,
      });
    } catch (error) {
      console.error(error);
      messageApi.error(getErrorMessage(error, 'applicantsComp.rejectError'));
    }
  };

  if (isLoading) {
    return (
      <>
        {Array(6)
          .fill(null)
          .map((_, i) => {
            return (
              <div style={{ paddingBottom: 10 }} key={i}>
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 2, width: ['20%', '100%'] }}
                ></Skeleton>
              </div>
            );
          })}
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <List
        itemLayout="horizontal"
        dataSource={applications}
        renderItem={(item) => {
          const applicant = applicants.find(
            (applicant) => applicant.id === item.studentId
          );
          const userName = applicant
            ? `${applicant.lastName} ${applicant.firstName}`.trim()
            : '';
          const internship = internships.find(
            (internsh) => internsh.id === item.internshipOfferId
          );
          const internshipName = internship ? internship.name : '';
          const orgId = internship ? internship.organizationId : 0;
          return (
            applications && (
              <List.Item
                actions={[
                  <Button
                    key={`accept-${item.id}`}
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() =>
                      onAccept(orgId, item.internshipOfferId, item.id)
                    }
                  >
                    {t('applicantsComp.accept')}
                  </Button>,
                  <Button
                    key={`reject-${item.id}`}
                    icon={<CloseOutlined />}
                    onClick={() =>
                      onReject(orgId, item.internshipOfferId, item.id)
                    }
                  >
                    {t('applicantsComp.reject')}
                  </Button>,
                ]}
              >
                <List.Item.Meta title={userName} description={internshipName} />
              </List.Item>
            )
          );
        }}
      />
    </>
  );
};

export default ApplicantsComponent;
